/**
 * Reducer, action constants, and typed state for the EditTicket view.
 *
 * The action *string values* are part of an external contract — they are
 * dispatched into this reducer from sibling components (e.g. `ResolutionNote`
 * dispatches `'set-resolution'`, and `EditTicketActions` / `TicketConversation`
 * receive the `ACTIONS` map and `dispatch` as props). Do **not** rename action
 * string values without updating those consumers.
 */

export const ACTIONS = {
  CALL_API: 'call-api',
  SUCCESS: 'success',
  ERROR: 'error',
  SAVING_STATE: 'saving-state',
  SAVING_SUCCESS: 'saving-success',
  SET_TICKET: 'set-ticket',
  SET_TICKETS: 'set-tickets',
  UPDATE_TICKET: 'update-ticket',
  SET_SIGNEDIN_USER: 'set-signedin-user',
  IS_GETTING_USER: 'is-getting-user',
  IS_GETTING_REQUESTER: 'is-getting-requester',
  SET_REQUESTER: 'set-requester',
  SET_ADMIN: 'set-admin',
  AUTHENTICATE_SUCCESS: 'authenticate-success',
  AUTHENTICATE_LOADING: 'authenticate-loading',
  NO_ACCESS: 'no_access',
  TAKING_TICKET: 'taking_ticket',
  TAKING_TICKET_SUCCESS: 'taking-ticket-success',
  UNASSIGNING: 'unassigning',
  UNASSIGNING_SUCCESS: 'unassigning-success',
  EDITTING: 'editting',
  DONE_EDITTING: 'done-editting',
  APPLY_TEMPLATE: 'apply-template',
  APPLY_TEMPLATE_SUCCESS: 'apply-template-success',
  SET_FILE_CHANNEL: 'set-file-channel',
  SET_TICKET_SLAS: 'set-ticket-SLAs',
  APPLY_LIFECYCLE: 'apply-lifecycle',
  APPLY_LIFECYCLE_SUCCESS: 'apply-lifecycle-success',
  APPLY_LIFECYCLE_TICKET: 'apply-lifecycle-ticket',
  RESET_UPDATE_TICKET: 'reset-update-ticket',
  SET_DEFAULT_TICKET: 'set-default-ticket',
  SET_RESOLUTION: 'set-resolution',
  PULL_GROUPCHAT: 'pullGroupChat',
  SEND_TEMPLATEFORM: 'send-template-form',
  SET_RELOAD_ATTACHMENTS: 'reload-attachments',
  SET_TOTAL_AGENT_TIME: 'set-total-agent-time'
} as const;

export type ActionType = typeof ACTIONS[keyof typeof ACTIONS];

export interface EditTicketState {
  hasAccess: boolean;
  isSavingState: boolean;
  signedInUser: any;
  ticket: any;
  isLoading: boolean;
  isGettingSignedInUser: boolean;
  isGettingRequester: boolean;
  requester: any;
  isAdminOrAnalyst: boolean;
  isTakingTicket: boolean;
  isUnassigning: boolean;
  isEditting: boolean;
  isApplyTemplateOpen: boolean;
  isApplyLifecycleOpen: boolean;
  fileTriggerChannel: any;
  resetUpdateTicketState: boolean;
  ResolutionCategories: any[];
  updatedTicket: any;
  defaultTicket: any;
  isSendTemplateFormOpen: boolean;
  reloadAttachments: boolean;
  /**
   * Live Total Agent Time, deliberately held beside `ticket` rather than inside it.
   * See the SET_TOTAL_AGENT_TIME case for why. Undefined until a total is reported for
   * this ticket, at which point the detail card prefers it over the loaded ticket's
   * values. `modifiedDate` is absent when that total came from a load rather than from a
   * change made on this page.
   */
  agentTimeUpdate?: { totalMinutes: number; modifiedDate?: string };
  // additional fields populated by some action handlers
  tickets?: any;
  UpdatedTicket?: any;
}

/**
 * Loose action shape: external dispatchers send `{ type, data }` objects with
 * various data shapes. We keep `data: any` here because the contract spans
 * several files; tightening per-action requires a coordinated change to
 * `ResolutionNote`, `EditTicketActions`, etc.
 */
export interface EditTicketAction {
  type: ActionType | string;
  data?: any;
}

export const editPageInitialState: EditTicketState = {
  hasAccess: true,
  isSavingState: false,
  signedInUser: {},
  ticket: {},
  isLoading: true,
  isGettingSignedInUser: true,
  isGettingRequester: true,
  requester: {},
  isAdminOrAnalyst: false,
  isTakingTicket: false,
  isUnassigning: false,
  isEditting: false,
  isApplyTemplateOpen: false,
  isApplyLifecycleOpen: false,
  fileTriggerChannel: '',
  resetUpdateTicketState: false,
  ResolutionCategories: [],
  updatedTicket: {},
  defaultTicket: {},
  isSendTemplateFormOpen: false,
  reloadAttachments: false
};

export const editPageReducer = (state: EditTicketState, action: EditTicketAction): EditTicketState => {
  switch (action.type) {
    case ACTIONS.SET_RELOAD_ATTACHMENTS:
      return { ...state, reloadAttachments: action.data };

    case ACTIONS.PULL_GROUPCHAT:
      return {
        ...state,
        ticket: {
          ...state.ticket,
          GroupChat: action.data,
          GroupChatId: action.data.Id
        }
      };

    case ACTIONS.SAVING_STATE:
      return { ...state, isSavingState: true };

    case ACTIONS.SAVING_SUCCESS:
      return { ...state, isSavingState: false };

    case ACTIONS.SET_TICKETS:
      return { ...state, tickets: action.data };

    case ACTIONS.UPDATE_TICKET:
      return {
        ...state,
        UpdatedTicket: { ...state.UpdatedTicket, ...action.data }
      };

    case ACTIONS.SET_TICKET:
      return { ...state, ticket: action.data };

    case ACTIONS.SET_RESOLUTION:
      return {
        ...state,
        ticket: { ...state.ticket, Resolution: action.data }
      };

    // Kept out of `ticket` on purpose. `state.ticket` is handed straight to
    // EditTicketForm, and its `[props.ticket]` effect re-syncs the whole form and clears
    // UpdatedTicket on every identity change - so merging the total in here threw away
    // whatever property edit the user had staged but not yet saved. The detail card reads
    // this override instead, and `ticket` is left alone.
    //
    // The modified stamp rides along when the change was made here, because recording time
    // modifies the ticket server-side; without it the header would show a new total beside
    // an older timestamp. Both are the client's approximation until the next load, which is
    // what SET_DEFAULT_TICKET clearing this restores.
    //
    // A total reported by a plain load carries no stamp: it may be another agent's time,
    // logged at a moment this page cannot know. The stamp already held is kept rather than
    // cleared, so time logged here earlier is not un-stamped by a later reload of the tab.
    case ACTIONS.SET_TOTAL_AGENT_TIME:
      return {
        ...state,
        agentTimeUpdate: {
          totalMinutes: action.data.totalMinutes,
          modifiedDate: action.data.modifiedDate ?? state.agentTimeUpdate?.modifiedDate
        }
      };

    case ACTIONS.CALL_API:
      return { ...state, isLoading: true };

    case ACTIONS.SUCCESS:
      return { ...state, isLoading: false };

    case ACTIONS.SET_SIGNEDIN_USER:
      return { ...state, signedInUser: action.data, isGettingSignedInUser: false };

    case ACTIONS.IS_GETTING_USER:
      return { ...state, isGettingSignedInUser: true };

    case ACTIONS.SET_REQUESTER:
      return { ...state, requester: action.data, isGettingRequester: false };

    case ACTIONS.SET_ADMIN:
      return { ...state, isAdminOrAnalyst: true };

    case ACTIONS.NO_ACCESS:
      return { ...state, hasAccess: false };

    case ACTIONS.TAKING_TICKET:
      return { ...state, isTakingTicket: true };

    case ACTIONS.TAKING_TICKET_SUCCESS:
      return { ...state, isTakingTicket: false };

    case ACTIONS.UNASSIGNING:
      return { ...state, isUnassigning: true };

    case ACTIONS.UNASSIGNING_SUCCESS:
      return { ...state, isUnassigning: false };

    case ACTIONS.EDITTING:
      return { ...state, isEditting: true };

    case ACTIONS.DONE_EDITTING:
      return { ...state, isEditting: false };

    case ACTIONS.APPLY_TEMPLATE:
      return { ...state, isApplyTemplateOpen: true };

    case ACTIONS.APPLY_TEMPLATE_SUCCESS:
      return { ...state, isApplyTemplateOpen: false };

    case ACTIONS.SET_FILE_CHANNEL:
      return { ...state, fileTriggerChannel: action.data };

    case ACTIONS.SET_TICKET_SLAS:
      return {
        ...state,
        ticket: { ...state.ticket, TicketSlas: action.data }
      };

    case ACTIONS.APPLY_LIFECYCLE:
      return { ...state, isApplyLifecycleOpen: true };

    case ACTIONS.APPLY_LIFECYCLE_SUCCESS:
      return { ...state, isApplyLifecycleOpen: false };

    case ACTIONS.RESET_UPDATE_TICKET:
      return { ...state, resetUpdateTicketState: action.data };

    case ACTIONS.APPLY_LIFECYCLE_TICKET:
      return {
        ...state,
        ticket: {
          ...state.ticket,
          TicketLifecycle: action.data,
          TicketLifecycleId: action.data['Id']
        }
      };

    // Dispatched once per ticket load, so it is the point at which the server's own
    // total supersedes anything logged during the previous page life.
    case ACTIONS.SET_DEFAULT_TICKET:
      return {
        ...state,
        agentTimeUpdate: undefined,
        defaultTicket: { ...state.defaultTicket, ...action.data }
      };

    case ACTIONS.SEND_TEMPLATEFORM:
      return { ...state, isApplyTemplateOpen: true, isSendTemplateFormOpen: true };

    default:
      throw new Error(`Unexpected action: ${action.type}`);
  }
};
